import { questionCount } from "./openQuestions.ts";
import { IN_PROGRESS, OKTHX_IS_WORKING, OKTHX_NEEDS_YOU } from "./taskRowCopy.ts";
import type { UserTask } from "@/types/userTasks.ts";

/**
 * Whose move it is on a task, which is not the same question as whose task it
 * is.
 *
 * The two agree for most of a task's life and come apart exactly where it
 * matters: a task okthx owns while it waits on the reader is okthx's by
 * assignment and the reader's to answer.
 *
 * - `blocked` — okthx holds it and has asked the reader something.
 * - `held` — the reader is on it themselves, away from the app.
 * - `waiting` — parked on someone else answering.
 * - `okthx` — okthx is working on it, uninterrupted.
 * - `yours` — nobody is in the way.
 */
export type TaskHolderState =
  | { kind: "blocked"; questions: number }
  | { kind: "held" }
  | { kind: "waiting"; name: string | null }
  | { kind: "okthx" }
  | { kind: "yours" };

/**
 * Which of the four a task is in, or `null` where the question does not apply.
 *
 * Shared because the order of these tests is load-bearing and two copies of it
 * would drift. They are read in the order `GROUPS` reads them, so a holder
 * line cannot contradict the heading above it: a task okthx owns while it
 * waits on someone else can still be stopped on a question for the reader, and
 * `GROUPS` files that under Needs your input because answering is what
 * unblocks it. Reading `waitingOn` first put Priya's face on a card headed
 * "Needs your input".
 *
 * `questions` arrives a beat after the task — it is read from the thread — so
 * callers pass what they have and the label falls back to the general line
 * rather than to a blank.
 */
export function taskHolderState(
  task: Pick<UserTask, "status" | "assignee" | "needsInput" | "waitingOn" | "heldAtMs">,
  questions?: number,
): TaskHolderState | null {
  if (task.status !== "open") return null;
  if (task.assignee === "okthx" && task.needsInput) {
    return { kind: "blocked", questions: questions ?? 0 };
  }
  // A hold and a wait never share a task: the server ends one when the other
  // is written. So the order here is only for the type's sake.
  if (task.heldAtMs) return { kind: "held" };
  if (task.waitingOn) return { kind: "waiting", name: task.waitingOn.name ?? null };
  if (task.assignee === "okthx") return { kind: "okthx" };
  return { kind: "yours" };
}

/**
 * What the holder line says.
 *
 * On a blocked task it says what is actually wanted where the thread has told
 * us: "needs your input" is true of every blocked task and tells the reader
 * nothing about what is being asked of them.
 */
export function taskHolderLabel(state: TaskHolderState): string {
  switch (state.kind) {
    case "blocked":
      return state.questions > 0 ? `${questionCount(state.questions)} for you` : OKTHX_NEEDS_YOU;
    case "held":
      return IN_PROGRESS;
    case "waiting":
      return state.name ? `Waiting on ${state.name}` : "Waiting on someone";
    case "okthx":
      return OKTHX_IS_WORKING;
    case "yours":
      return "Yours";
  }
}
